import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import { showUndoToast } from "@nexus/ui";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useUpdateOrderScriptFulfillment } from "@/lib/queries";

import {
  planBulkStatusChange,
  type ScriptSelection,
} from "../../lib/orders-list-model";
import { resolveRevisionDeliverableId } from "../../lib/revision-deliverable";
import type { MarketplaceOrder } from "../../types/marketplace.types";

/**
 * Apply one status to every selected script through the same endpoint so
 * revision notes, deadlines, activity, and cache behavior remain consistent.
 */
export function useBulkStatusChange(
  scope: string | { admin: true },
) {
  const { t } = useTranslation();
  const updateScript = useUpdateOrderScriptFulfillment();
  const [applying, setApplying] = useState(false);
  const isAdmin = typeof scope !== "string";
  const tenantId = typeof scope === "string" ? scope : undefined;

  const apply = useCallback(
    async (
      orders: readonly MarketplaceOrder[],
      selection: ScriptSelection,
      fulfillmentStatus: MarketplaceFulfillmentStatus,
    ): Promise<boolean> => {
      const plan = planBulkStatusChange(orders, selection);
      if (plan.scripts.length === 0 || applying) return false;

      setApplying(true);
      return new Promise<boolean>((resolve) => {
        const execute = async () => {
          let failed = 0;
          try {
            // Serialize updates so whole-order cache responses cannot overwrite
            // a newer sibling-script response that completed first.
            for (const { order, script } of plan.scripts) {
              try {
                await updateScript.mutateAsync({
                  orderId: order.id,
                  orderScriptId: script.id,
                  tenantId: tenantId ?? order.tenantId,
                  fulfillmentStatus,
                  revisionNote:
                    fulfillmentStatus === "SENT_FOR_REVISION"
                      ? undefined
                      : null,
                  deliverableId:
                    fulfillmentStatus === "SENT_FOR_REVISION"
                      ? resolveRevisionDeliverableId(script, null)
                      : undefined,
                  suppressErrorToast: true,
                  admin: isAdmin || undefined,
                });
              } catch {
                failed += 1;
              }
            }

            if (failed === 0) {
              toast.success(
                t("marketplace.orders.list.bulk.success", {
                  count: selection.size,
                }),
              );
              resolve(true);
              return;
            }
            toast.error(
              t("marketplace.orders.list.bulk.partialFailure", {
                failed,
                total: plan.scripts.length,
              }),
            );
            resolve(false);
          } finally {
            setApplying(false);
          }
        };

        showUndoToast({
          message: (seconds) =>
            t("marketplace.orders.list.bulk.confirm", {
              count: selection.size,
              seconds,
            }),
          duration: 6,
          undoLabel: t("marketplace.orders.list.bulk.undo"),
          onConfirm: () => void execute(),
          onUndo: () => {
            setApplying(false);
            toast.success(t("marketplace.orders.list.bulk.cancelled"));
            resolve(false);
          },
        });
      });
    },
    [applying, isAdmin, t, tenantId, updateScript],
  );

  return { apply, applying };
}
